import { useCallback, useEffect, useRef, useState } from "react";
import { offlineDB } from "../data/offlineDB.js";

export default function useOfflineJobWizard(userId) {
  const storageKey = userId ? `job-create-${userId}` : null;

  const [savedState, setSavedState] = useState(null);
  const [isRestoring, setIsRestoring] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const mountedRef = useRef(true);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!storageKey) {
      setIsRestoring(false);
      setSavedState(null);
      return;
    }

    let active = true;
    setIsRestoring(true);

    offlineDB.jobWizard
      .get(storageKey)
      .then((entry) => {
        if (!active || !mountedRef.current) return;
        if (entry?.data) setSavedState(entry.data);
        setIsRestoring(false);
      })
      .catch(() => {
        if (mountedRef.current) {
          setSavedState(null);
          setIsRestoring(false);
        }
      });

    return () => {
      active = false;
    };
  }, [storageKey]);

  const debouncedSave = useCallback(
    (newData) => {
      if (!storageKey) return;
      // Clear any pending save
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      debounceTimerRef.current = setTimeout(async () => {
        try {
          setIsSaving(true);
          await offlineDB.jobWizard.put({
            key: storageKey,
            data: newData,
            updatedAt: new Date(),
          });
          if (mountedRef.current) setLastSavedAt(new Date());
        } catch (err) {
          console.error("Failed to save job wizard data offline:", err);
        } finally {
          if (mountedRef.current) setIsSaving(false);
        }
      }, 2000);
    },
    [storageKey]
  );

  const saveProgress = (data) => {
    if (!storageKey) return;
    debouncedSave(data);
  };

  const clearProgress = async () => {
    if (!storageKey) return;

    // Cancel any pending debounced save FIRST
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    try {
      await offlineDB.jobWizard.delete(storageKey);
      setSavedState(null);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  return {
    savedState,
    isRestoring,
    saveProgress,
    clearProgress,
    isSaving,
    lastSavedAt,
  };
}